import { SESSION_TOKEN_EXPIRATION_HOURS, User } from '@arpeggi/shared'
import { AxiosRequestConfig } from 'axios'
import cookies from 'browser-cookies'
import { Buffer } from 'buffer'
import moment from 'moment'
import { useCallback, useEffect, useRef } from 'react'
import { useAccount, useConnect, useSignMessage } from 'wagmi'
import { useAuthAPI } from '../api/v2/AuthAPI'
import { unauthenticatedCreateUser } from '../api/v2/UsersAPI'
import { getLastWalletConnector } from '../components/auth/getLastWalletConnector'
import { appcuesIdentify } from '../lib/appcues'
import axios from '../lib/axios'
import { segmentButtonClick, segmentIdentifyUser } from '../lib/segment'
import { WalletProviderType } from '../lib/wagmi/connectors'
import authStore from '../stores/AuthStore'
import { log, LogCategories } from '../utils/logger'

export const useAuth = () => {
  const walletAddressRef = useRef<string>(null)
  const { getAuthInfo } = useAuthAPI()
  const { signMessageAsync } = useSignMessage()
  const {
    user,
    isLoggedIn,
    isLoggingIn,
    setIsLoggedIn,
    setIsLoggingIn,
    setUser,
    setIsUserLoading,
    isUserLoading
  } = authStore
  const { connectAsync, data: connectorData } = useConnect()
  const {
    address: walletAddress,
    isConnected: isEthereumProviderActive,
    isDisconnected
  } = useAccount()

  // updates to walletAddress are not available inside login unless I store it in a ref like this
  useEffect(() => {
    walletAddressRef.current = walletAddress

    if (user && walletAddress && user.wallet_address !== walletAddress) {
      getAndSetUser()
    }
  }, [walletAddress])

  const getLoggedInCookie = (): boolean => {
    log.info({ category: LogCategories.AUTH }, 'getLoggedInCookie - start')

    const loggedInCookie = cookies.get('session') != null

    log.info(
      { category: LogCategories.AUTH, data: { loggedInCookie } },
      'getLoggedInCookie - finish'
    )

    return loggedInCookie
  }

  /**
   * Checks if the session cookie exists, if not signs the wallet again and sets a new one
   */
  const getOrSetUserAuthToken = async (): Promise<string | null> => {
    log.info(
      { category: LogCategories.AUTH, data: { walletAddress } },
      'getOrSetUserAuthToken - start'
    )

    const sessionCookie = getUserAuthToken()

    if (sessionCookie != null) {
      log.info(
        { category: LogCategories.AUTH, data: { sessionCookie, walletAddress } },
        'getOrSetUserAuthToken - success'
      )

      return sessionCookie
    }

    if (walletAddressRef.current === null) {
      log.info(
        { category: LogCategories.AUTH, data: { walletAddress: walletAddressRef.current } },
        'getOrSetUserAuthToken - walletAddress fail'
      )
      setIsLoggingIn(false)
      setIsLoggedIn(false)

      return null
    }

    const signResult = await getLoginSignature()

    if (signResult === null) {
      log.info(
        { category: LogCategories.AUTH, data: { signResult } },
        'getOrSetAuthToken - signResult fail'
      )
      setIsLoggingIn(false)
      setIsLoggedIn(false)

      return null
    }

    setUserAuthToken({ ...signResult, walletAddress: walletAddressRef.current })

    log.info(
      { category: LogCategories.AUTH, data: { signResult, walletAddress } },
      'getOrSetUserAuthToken - success'
    )

    return getUserAuthToken()
  }

  const setWalletProviderCookie = (walletProvider: WalletProviderType) => {
    log.info({ category: LogCategories.AUTH }, 'setWalletProviderCookie - start')

    cookies.set('walletProvider', walletProvider)

    log.info(
      { category: LogCategories.AUTH, data: { walletProvider } },
      'setWalletProviderCookie - success'
    )
  }

  const getWalletProviderCookie = () => {
    log.info({ category: LogCategories.AUTH }, 'getWalletProviderCookie - start')
    const lastWalletProvider = cookies.get('walletProvider')

    if (lastWalletProvider === null) {
      log.info(
        { category: LogCategories.AUTH, data: { lastWalletProvider } },
        'getWalletProviderCookie - fail'
      )
    } else {
      log.info(
        { category: LogCategories.AUTH, data: { lastWalletProvider } },
        'getWalletProviderCookie - success'
      )
    }

    return lastWalletProvider
  }

  /**
   * Gets the user object from the server and sets it locally to the authStore
   * @returns userProfile
   */
  const getAndSetUser = async (): Promise<User | null> => {
    log.info(
      { category: LogCategories.AUTH, data: { walletAddress: walletAddressRef.current } },
      'getAndSetUser - start'
    )
    setIsUserLoading(true)

    try {
      let user: User
      try {
        const { data: retrievedUser } = await axios.get<User>(
          `/api/v2/users/${walletAddressRef.current}`
        )

        user = retrievedUser
      } catch (err) {}

      if (!user) {
        user = await unauthenticatedCreateUser({
          walletAddress: walletAddressRef.current
        })
      }

      setUser(user)
      segmentIdentifyUser(user)

      window.Appcues?.identify(user.walletAddress)
      log.info(
        { category: LogCategories.AUTH, data: { walletAddress, user } },
        'getAndSetUser - success'
      )

      setIsUserLoading(false)
      return user
    } catch (error) {
      console.error(error)
      log.info(
        { category: LogCategories.AUTH, data: { wallet: walletAddressRef.current } },
        'getAndSetUser - profileData fail'
      )
      setIsLoggingIn(false)
      setIsLoggedIn(false)
      setIsUserLoading(false)

      return null
    }
  }

  /**
   *  Attempts to silently log in the user if possible
   */
  const silentLogin = async () => {
    log.info({ category: LogCategories.AUTH, data: walletAddress }, 'login - start')

    setIsLoggingIn(true)
    setIsLoggedIn(false)

    const lastWalletProvider = getWalletProviderCookie()

    if (lastWalletProvider === null || lastWalletProvider === 'unknown') {
      setIsLoggingIn(false)
      appcuesIdentify()
      return
    }

    const connector = getLastWalletConnector()

    if (!connector) {
      setIsLoggingIn(false)
      log.info({ category: LogCategories.AUTH }, 'login - provider fail')
      appcuesIdentify()
      return
    }
    if (isDisconnected) await connectAsync({ connector })

    if (walletAddressRef.current === null) {
      log.info(
        { category: LogCategories.AUTH, data: { walletAddress: walletAddressRef.current } },
        'login - walletConnect fail'
      )
      setIsLoggingIn(false)
      appcuesIdentify()
      return
    }

    const user = await getAndSetUser()

    if (user === null) {
      setIsLoggingIn(false)
      log.info({ category: LogCategories.AUTH }, 'login - user has no profile')
      appcuesIdentify()
      return
    }

    log.info({ category: LogCategories.AUTH }, 'login - success')
    setIsLoggingIn(false)
    setIsLoggedIn(true)
  }

  /**
   * Fully logs out the user, deleting all cookies created, currently does not deactivate the wallet (may change)
   */
  const logout = useCallback(() => {
    log.info({ category: LogCategories.AUTH }, 'logout - start')
    segmentButtonClick('Log Out')
    setUser(null)
    cookies.erase(`session`)
    cookies.erase('current_wallet')
    cookies.erase('walletProvider')
    setIsLoggedIn(false)
    log.info({ category: LogCategories.AUTH }, 'logout - success')
  }, [])

  /**
   * Will ask the user to sign the given transaction
   */
  const getLoginSignature = async () => {
    log.info({ category: LogCategories.AUTH }, 'signWalletAddress - start')

    const { signingMessage } = await getAuthInfo(walletAddress)

    if (signingMessage === null) {
      log.info({ category: LogCategories.AUTH }, 'signWalletAddress - fail')

      return null
    }

    const nonce = signingMessage.nonce
    const message = signingMessage.message
    const signature = await signMessageAsync({ message })

    log.info(
      { category: LogCategories.AUTH, data: { signature, nonce, walletAddress } },
      'signWalletAddress - success'
    )

    return { signature, nonce, walletAddress }
  }

  /**
   * Replace axios.get with authenticatedGet for backend
   * functions that require authentication
   */
  const authenticatedGet = async <T = any>(url: string, params: any = {}) => {
    const headers = await getAuthHeadersOrPrompt()

    return axios.get<T>(url, { params, headers })
  }

  /**
   * Replace axios.post with authenticatedPost for backend
   * functions that require authentication.
   *
   * @param allowLargeUploads if true, sets the max maxBodyLength and maxContentLength to max values. Without this, max body size is 10 mb.
   */
  const authenticatedPost = async <T = any>(
    url: string,
    postData: any = {},
    allowLargeUploads: boolean = false
  ) => {
    let config: AxiosRequestConfig = {}
    config.headers = await getAuthHeadersOrPrompt()

    if (allowLargeUploads) {
      config.maxContentLength = 100000000
      config.maxBodyLength = 1000000000
    }

    return axios.post<T>(url, postData, config)
  }

  const getAuthHeadersOrPrompt = async () => {
    const token = await getOrSetUserAuthToken()

    return { Authorization: 'Token ' + token }
  }

  const authenticatedPatch = async <T = any>(url: string, body: any = {}) => {
    const headers = await getAuthHeadersOrPrompt()

    return axios.patch<T>(url, body, { headers })
  }

  const authenticatedPut = async <T = any>(url: string, body: any = {}) => {
    const headers = await getAuthHeadersOrPrompt()

    return axios.put<T>(url, body, { headers })
  }

  /**
   * Replace axios.delete with authenticatedDelete for backend
   * functions that require authentication
   */
  const authenticatedDelete = async <T = any>(url: string, data?: any) => {
    const headers = await getAuthHeadersOrPrompt()

    return axios.delete<T>(url, { headers, data })
  }

  return {
    getAndSetUser,
    getLoggedInCookie,
    getWalletProviderCookie,
    setWalletProviderCookie,
    getOrSetUserAuthToken,
    silentLogin,
    getLoginSignature,
    logout,
    isLoggedIn,
    isLoggingIn,
    user,
    setUser,
    authenticatedGet,
    authenticatedPost,
    authenticatedPatch,
    authenticatedPut,
    authenticatedDelete,
    isUserLoading,
    currentWalletAddress: walletAddress
  }
}

export const setUserAuthToken = ({
  signature,
  nonce,
  walletAddress
}: {
  signature: string
  nonce: string
  walletAddress: string
}) => {
  log.info({ category: LogCategories.AUTH }, 'setUserAuthToken - start')

  const data = JSON.stringify({
    signature,
    nonce,
    wallet_address: walletAddress
  })
  const encodedData = btoa(data)

  log.info({ category: LogCategories.AUTH, data: encodedData }, 'setUserAuthToken')

  cookies.set('session', encodedData, {
    expires: moment(Date.now()).add(SESSION_TOKEN_EXPIRATION_HOURS, 'hour').toDate()
  })

  log.info({ category: LogCategories.AUTH, encodedData }, 'setUserAuthToken - success')
}

export const isSessionCookieExpired = () => {
  log.info({ category: LogCategories.AUTH }, 'isSessionCookieExpired - start')
  const sessionCookie = cookies.get('session')

  if (sessionCookie === null) {
    log.info({ category: LogCategories.AUTH }, 'isSessionCookieExpired - success, no sessionCookie')
    return false
  }

  const decodedSessionCookie = JSON.parse(Buffer.from(sessionCookie, 'base64').toString()) as {
    signature: string
    walletAddres: string
    nonce: string
  }

  const split_nonce = decodedSessionCookie.nonce.split('-')
  const timestamp = moment(parseInt(String(split_nonce[split_nonce.length - 1]), 16))

  log.info({ category: LogCategories.AUTH, data: { timestamp } }, 'isSessionCookieExpired ')

  const isExpired =
    moment.duration(moment().diff(timestamp)).as('hours') >= SESSION_TOKEN_EXPIRATION_HOURS

  log.info(
    { category: LogCategories.AUTH, data: { isExpired } },
    'isSessionCookieExpired - success'
  )

  return isExpired
}

/**
 * Gets the user auth token (session cookie), returns the cookie data or null if it doesnt exist
 */
const getUserAuthToken = () => {
  log.info({ category: LogCategories.AUTH }, 'getUserAuthToken - start')

  const sessionCookie = cookies.get('session')

  if (sessionCookie == null)
    log.info({ category: LogCategories.AUTH, data: { sessionCookie } }, 'getUserAuthToken - fail')
  else
    log.info(
      { category: LogCategories.AUTH, data: { sessionCookie } },
      'getUserAuthToken - success'
    )

  return sessionCookie
}

const getAuthHeaders = async ({ isSilent }: { isSilent?: boolean } = {}) => {
  const token = getUserAuthToken()

  return token ? { Authorization: 'Token ' + token } : {}
}

export const silentAuthenticatedGet = async <T = any>(url: string, params: any = {}) => {
  let config: AxiosRequestConfig = { params }

  try {
    config.headers = await getAuthHeaders()
  } finally {
    return axios.get<T>(url, config)
  }
}

export const silentAuthenticatedPost = async <T = any>(
  url: string,
  postData: any = {},
  allowLargeUploads: boolean = false
) => {
  let config: AxiosRequestConfig = {}

  try {
    config.headers = await getAuthHeaders()
  } finally {
    if (allowLargeUploads) {
      config.maxContentLength = 100000000
      config.maxBodyLength = 1000000000
    }

    return axios.post<T>(url, postData, config)
  }
}
